import React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { PaperClipIcon } from '@heroicons/react/20/solid'
import { getQueryParamValue } from '../../../utils/general'
import { fabricMap, getDisplayLabel, getKeysByTitle, isHTMLContent, isSpecialKey, mediaMap, shouldApplyIndentFormat, shouldApplySpecialFormat, validateEasyViewKey } from '../../../utils/artDetailsUtils'
import useDivHeights from '../../../utils/useDivHeights'
import { isPlayerOrLNProduct, LNTransferDetailsGenerator } from '../../../utils/LNTransferDetailsGenerator'
import LNDetails from './lnDetails'
import { docLabelMap, WizardDocData } from './artDetails/artDetailsContent'
import { useArtDetails } from './artDetails/useArtDetails'
import BarcelonaDetails from './barcelonaDetails'
import EmblemWizardDetails from './ewDetails'
import { EmbroideryNote, hasNonEmptyNote, NotesCard } from './notesCard'
import { getBackendApiUrl } from '../../../config'
import { addAuthorizationHeader, getAdminId } from '../../../api/network'
import EditableInput from '../../../components/editableInput/editableInput'
import { WizardDocNote } from '../../../types/api-types'
import { renderValue } from '../../../components/cardDetails/valueRender'
import { greekLetters, letters, numbers } from './rosterGrid'
import { PackContentsTable } from './artDetails/packContentCard'

// Styles
import styles from './info.module.css'
import NhlDetails from './nhlDetails'

interface Attachment {
  name: string;
  size: string;
  downloadLink: string;
}

export interface InfoProps {
  about?: string;
  attachments?: Attachment[];
  isLNTransfer: boolean;
  isEasyView: boolean;
  isWizardDesign: boolean;
  isBarcelona: boolean;
  isNhl: boolean;
  isEmblemWizard: boolean;
  originalDocId?: string;
  productType: string;
  fullWizardData?: any;
  extraDocData?: WizardDocData;
  internalNotes: WizardDocNote[];
  [key: string]: any;
}

const AttachmentList = ({ attachments }: { attachments: Attachment[] }) => (
  <ul role="list" className="divide-y divide-gray-100 rounded-md border border-gray-200">
    {attachments.map((attachment, index) => (
      <li key={index} className="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
        <div className="flex w-0 flex-1 items-center">
          <PaperClipIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <div className="ml-4 flex min-w-0 flex-1 gap-2">
            <span className="truncate font-medium">{attachment.name}</span>
            <span className="flex-shrink-0 text-gray-400">{attachment.size}</span>
          </div>
        </div>
        <div className="ml-4 flex-shrink-0">
          <a href={attachment.downloadLink} className="font-medium text-indigo-600 hover:text-indigo-500">
            Download
          </a>
        </div>
      </li>
    ))}
  </ul>
);

type ParsedExtendedDescriptionRow = {
  colour: string;
  elevation: string;
};

type ParsedExtendedDescription = {
  fields: { label: string; value: string }[];
  rows: ParsedExtendedDescriptionRow[];
};

const decodeExtendedDescriptionText = (value: string): string => {
  if (!value) return '';

  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .trim();
};

const parseExtendedDescription = (value?: string): ParsedExtendedDescription => {
  const text = decodeExtendedDescriptionText(value ?? '');
  if (!text) {
    return { fields: [], rows: [] };
  }

  const lines = text.split('\n').map(line => line.trim());
  const fields: { label: string; value: string }[] = [];
  const rows: ParsedExtendedDescriptionRow[] = [];

  let tableStarted = false;

  for (const line of lines) {
    if (!line) {
      continue;
    }

    if (/^colour\s*\|\s*elevation/i.test(line) || /^color\s*\|\s*elevation/i.test(line)) {
      tableStarted = true;
      continue;
    }

    if (tableStarted) {
      if (/^---\s*\|\s*---$/.test(line)) {
        continue;
      }

      const rowMatch = line.match(/^(.+?)\s*\|\s*(.+)$/);
      if (rowMatch) {
        rows.push({
          colour: rowMatch[1].trim(),
          elevation: rowMatch[2].trim(),
        });
      }
      continue;
    }

    const fieldMatch = line.match(/^([^:]+):\s*(.+)$/);
    if (fieldMatch) {
      fields.push({
        label: fieldMatch[1].trim(),
        value: fieldMatch[2].trim(),
      });
    }
  }

  return { fields, rows };
};

export default function Info({
  attachments,
  about,
  isLNTransfer,
  isEasyView,
  isWizardDesign,
  isBarcelona,
  isNhl,
  isEmblemWizard,
  originalDocId,
  productType,
  fullWizardData,
  extraDocData,
  internalNotes,
  ...restProps
}: InfoProps) {
  const showLNTrasnferDetails = isLNTransfer && Object.keys(LNTransferDetailsGenerator).includes(productType)
  const artIdParam = getQueryParamValue("artId");
  const currentUrl = new URL(window.location.href);
  const originalDocIsDifferent = originalDocId !== artIdParam;
  const artNotes: string = restProps['artNotes'];
  const specialInstructions: string|undefined = isEasyView ? restProps['Notes'] : undefined;
  const sewFileEmbroideryNotes = restProps['sewFileEmbroideryNotes'] // Customer Note
  const extendedDescriptionSource =
    fullWizardData?.extendedData?.extendedDescription ??
    fullWizardData?.extendedData?.extendedDescriptionHTML ??
    restProps['extendedDescription'] ??
    restProps['extendedDescriptionHTML'];
  const extendedDescriptionText = typeof extendedDescriptionSource === 'string' ? decodeExtendedDescriptionText(extendedDescriptionSource) : '';
  const parsedExtendedDescription = useMemo(
    () => parseExtendedDescription(extendedDescriptionText),
    [extendedDescriptionText]
  );
  const isLiquid3DItem = useMemo(() => (
    parsedExtendedDescription.fields.some(field => field.label === 'Product Type' && field.value === 'Liquid 3D')
    || /_LIQUID_3D/i.test(extendedDescriptionText)
  ), [extendedDescriptionText, parsedExtendedDescription.fields]);
  const {
    sewFileInternalEmbroideryNotes: note,
    sewFileInternalEmbroideryNotesDate: date,
    sewFileInternalEmbroideryNotesBy: createdBy,
  } = restProps;
  const embroideryNote: EmbroideryNote = { note, date, createdBy };
  const displayRoster = isPlayerOrLNProduct(productType) || isBarcelona;
  const adminId = getAdminId();
  const hasInternalNotes = (hasNonEmptyNote(embroideryNote) || internalNotes.length > 0) && adminId;
  const weiDesignNotes: string|undefined = adminId && restProps['WeiDesignNotes'];

  const shouldShowPackContents = useMemo(() => {
    const isValidServiceType = fullWizardData?.serviceType === 'CustomCutLetters'
      || fullWizardData?.serviceType === 'CustomCutNumbers';
    const hasQuantities = fullWizardData?.quantities &&
      typeof fullWizardData.quantities === 'object' &&
      Object.keys(fullWizardData.quantities).length > 0;

    if (!isValidServiceType || !hasQuantities) return false;

    const validCharacters = [...letters, ...numbers.map(String), ...greekLetters];
    const quantityKeys = Object.keys(fullWizardData.quantities);
    const hasValidKeys = quantityKeys.every(key => validCharacters.includes(key));

    return hasValidKeys && quantityKeys.length > 0;
  }, [productType, fullWizardData]);

  const handleArtNameChange = (designId: string, name: string) => {
    const headers = addAuthorizationHeader({
      'Content-Type': 'application/json'
    });

    return fetch(`${getBackendApiUrl()}/design-artwork-name`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        designId,
        name
      })
    }).then(async (processResp) => {
      if (!processResp.ok) throw new Error("Error changing artwork name " + await processResp.text())
      return
    });
  }

  const [maxHeight, setMaxHeight] = useState<number>(150);

  if (originalDocId && originalDocIsDifferent) {
    const paramsToRemove = ['DocNo', 'DocLineNo', 'Page'];
    paramsToRemove.forEach(param => currentUrl.searchParams.delete(param));
    currentUrl.searchParams.set('artId', originalDocId);
  }

  restProps = Object.assign(restProps.media ? { mediaText: mediaMap[restProps.media as keyof typeof mediaMap] ?? "" } : {}, restProps);
  restProps = Object.assign(restProps.requestedFabric ? { requestedFabricText: fabricMap[restProps.requestedFabric as keyof typeof fabricMap] ?? "" } : {}, restProps);

  const { mappedCards, processedDataByCards, getFieldGroupInfo } = useArtDetails(fullWizardData, restProps, isEasyView, isLNTransfer, artIdParam);

  const processedCardsArray = useMemo(() => {
    if (!processedDataByCards || processedDataByCards.size === 0) {
      return [];
    }

    return Array.from(processedDataByCards.entries())
      .map(([cardTitle, cardData]) => ({
        title: cardTitle,
        data: cardData
      }))
      .filter(card => {
        return Object.keys(card.data).length > 0 &&
          Object.values(card.data).some(value => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'string') return value.trim() !== '';
            return true;
          });
      });
  }, [processedDataByCards]);

  const { refs, heights } = useDivHeights(processedCardsArray?.length + (shouldShowPackContents ? 1 : 0));

  const productDetailsKeys = getKeysByTitle(processedCardsArray, "PRODUCT DETAILS");

  useEffect(() => {
    const max = Math.max(...heights);
    setMaxHeight(max);
  }, [heights]);

  function checkMappedCards(): string {
    const hasNotes = Boolean(artNotes || hasInternalNotes || sewFileEmbroideryNotes || specialInstructions || weiDesignNotes);
    const numMappedCards = processedCardsArray?.length ?? 0;
    const packContentsCard = shouldShowPackContents ? 1 : 0;

    const totalColumns = (showLNTrasnferDetails || isBarcelona)
      ? (displayRoster ? 2 : 1) + (hasNotes ? 1 : 0)
      : numMappedCards + packContentsCard + (hasNotes ? 1 : 0);

    const columnStyles: Record<number, string> = {
      5: styles.fiveColumns,
      4: styles.fourColumns,
      3: styles.threeColumns,
      2: styles.twoColumns,
      1: styles.oneColumn
    };

    return columnStyles[totalColumns] || styles.oneColumn;
  }

  const getFieldLabel = (key: string) => {
    for (const card of mappedCards) {
      const property = card.properties.find(prop => prop.key === key);
      if (property) return property.label;
    }

    for (const card of mappedCards) {
      const property = card.properties.find(prop => prop.key === key);
      if (property) return property.label;
    }

    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const hiddenDetailKeys = new Set([
    'extendedDescription',
    'extendedDescriptionHTML',
    'extendedDescriptionHtml',
    ...(isLiquid3DItem ? [
      'media',
      'mediaText',
      'media_original',
      'itemNo',
      'documentId',
    ] : []),
  ]);

  return (
    <>
      <div className={styles.infoWrapper}>
        {/* L&N Design Details */}
        {showLNTrasnferDetails && (
          <LNDetails
            productType={productType}
            fullWizardData={fullWizardData}
            displayRoster={displayRoster}
            checkMappedCards={checkMappedCards} />
        )}

        {/* Barcelona Design Details */}
        {isBarcelona && (
          <BarcelonaDetails
            fullWizardData={fullWizardData}
            displayRoster={displayRoster}
            checkMappedCards={checkMappedCards} />
        )}

        {/* Nhl Design Details */}
        {isNhl && (
          <NhlDetails
            fullWizardData={fullWizardData} />
        )}

        {/* Emblem wizard Design Details */}
        {isEmblemWizard &&
          <EmblemWizardDetails
            wizardData={fullWizardData}
          />
        }

        {/* Art Details */}
        {!showLNTrasnferDetails && !isBarcelona && !isNhl && !isEmblemWizard &&
          processedCardsArray.map((cardData, index) => (
            <div key={index} className={[styles.card, checkMappedCards()].join(' ')}>
              <h3 className={styles.title}>{cardData.title}</h3>
              <div className={styles.cardBody}
                ref={el => (refs.current[index] = el!)}>

                {originalDocId && index == 0 && originalDocIsDifferent && (
                  <div className={styles.details} key={"originalDocId"}>
                    <span className={styles.name}>Original Doc:</span>
                    <span className={styles.value}>
                      <a href={currentUrl.toString()} className="font-medium text-indigo-600 hover:text-indigo-500">
                        View Original Document
                      </a>
                    </span>
                  </div>
                )}

                {Object.entries(cardData.data).map(([key, value]) => {
                  if (hiddenDetailKeys.has(key)) return null;

                  const displayValue = renderValue(value);
                  if (displayValue === null) return null;

                  const fieldLabel = getFieldLabel(key);
                  if (isEasyView && !validateEasyViewKey(key)) return null;

                  const allKeys: string[] = Object.keys(cardData.data);
                  const groupInfo = getFieldGroupInfo(key, cardData.title, allKeys, productDetailsKeys);

                  const shouldIndent =
                    (cardData.title?.toLowerCase() === 'material selection details'
                      && !isEasyView
                      && isSpecialKey(key)
                      && !shouldApplySpecialFormat(key, productType))
                    || shouldApplyIndentFormat(key, productType);

                  const displayLabel = getDisplayLabel(key, fieldLabel, productType);

                  return (
                    <React.Fragment key={key}>
                      {(groupInfo.isGroupStart
                        && groupInfo.groups[0].name === groupInfo.groupName)
                        && <div className={styles.groupSpacer} />
                      }

                      <div
                        className={styles.details}
                        data-field={key}
                        style={{ marginLeft: shouldIndent ? '12px' : '0px' }}
                      >
                        <span className={styles.name}>
                          {key === 'quantities' && typeof value === 'object'
                            ? 'Total Quantity'
                            : displayLabel
                          }:
                        </span>
                        {fieldLabel === 'Artwork Name' ?
                          <span className={styles.value}>
                            <EditableInput
                              text={value as string}
                              onChange={changedText => handleArtNameChange(artIdParam ?? "", changedText)}
                            />
                          </span> :
                          isHTMLContent(displayValue.props?.value) ?
                            <span
                              className={styles.value}
                              dangerouslySetInnerHTML={{ __html: displayValue.props?.value }}
                            /> :
                            <span className={styles.value}>
                              {key === 'aspectRatio' && typeof value === 'number' && !isNaN(value) && isFinite(value)
                                ? value.toFixed(2)
                                : key === 'quantities' && typeof value === 'object'
                                  ? Object.values(value).reduce((sum: number, v) => sum + (Number(v) || 0), 0).toString()
                                  : displayValue
                              }
                            </span>
                        }
                      </div>

                      {groupInfo.isGroupEnd && (
                        <div className={styles.groupSpacer} />
                      )}
                    </React.Fragment>
                  );
                })}

                {cardData.title === 'PRODUCT DETAILS' && parsedExtendedDescription.fields.length > 0 && (
                  <>
                    {parsedExtendedDescription.fields.map((field, fieldIndex) => (
                      <div className={styles.details} key={`extended-description-field-${fieldIndex}`}>
                        <span className={styles.name}>{field.label}:</span>
                        <span className={styles.value}>{field.value}</span>
                      </div>
                    ))}

                    {parsedExtendedDescription.rows.length > 0 && (
                      <div className={styles.details} key="extended-description-table">
                        <div className="w-full overflow-hidden rounded-md border border-gray-200">
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="border-b border-gray-200 text-left">
                                <th className="py-2 pr-4 font-semibold text-gray-700">Colour</th>
                                <th className="py-2 font-semibold text-gray-700">Elevation (mm)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parsedExtendedDescription.rows.map((row, rowIndex) => (
                                <tr key={`${row.colour}-${rowIndex}`} className="border-b border-gray-100 last:border-b-0">
                                  <td className="py-2 pr-4 text-gray-900">{row.colour}</td>
                                  <td className="py-2 text-gray-900">{row.elevation}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Render doc data */}
                {extraDocData && index == 0 &&
                  Object.entries(extraDocData).map(([key, value]) => (
                    <div className={styles.details} key={key}>
                      <span className={styles.name}>{docLabelMap[key as keyof WizardDocData]}:</span>
                      <span className={styles.value}>{value}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          ))}

        {/* Pack Contents Card */}
        {shouldShowPackContents && (
          <div className={[styles.card, checkMappedCards()].join(' ')}>
            <PackContentsTable quantities={fullWizardData.quantities} />
          </div>
        )}

        {/* Art Notes Column */}
        {(artNotes || hasInternalNotes || sewFileEmbroideryNotes || specialInstructions || weiDesignNotes) &&
          (<div className={[styles.card, checkMappedCards(), showLNTrasnferDetails ? styles.bgWhite : ""].join(' ')}>
            <h3 className={styles.title}>NOTES</h3>
            <NotesCard
              artNotes={artNotes}
              embroideryNotes={embroideryNote}
              customerEmbroideryNote={sewFileEmbroideryNotes}
              internalNotes={internalNotes}
              specialInstructions={specialInstructions}
              maxHeight={maxHeight}
              weiDesignNotes={weiDesignNotes}
            />
          </div>
          )}
      </div>
      {/* Attachments */}
      <div>
        {attachments && attachments.length > 0 && (
          <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium leading-6 text-gray-900">Attachments</dt>
            <dd className="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
              <AttachmentList attachments={attachments} />
            </dd>
          </div>
        )}
      </div>
    </>
  );
}